import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subMonths} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {useSearchParams} from "react-router";
import useReportFiltersFetch from "../../../hooks/use-report-filters-fetch.js";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 6),
    dtEnd: new Date(),
    actions: [],
    programs: [],
    citizenId: null,
    createdByIds: [],
    responsibleIds: []
});

const filterLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    actions: "Ações",
    programs: "Programas de proteção",
    citizenId: "Protegido",
    createdByIds: "Cadastrado por",
    responsibleIds: "Responsável"
};

const initialTableColumns = {
    dt_action: {label: "D/H ação", visible: true},
    citizen_name: {label: "Cidadão/Empresa", visible: true},
    action: {label: "Ação", visible: true},
    description: {label: "Descrição", visible: true},
    created_by: {label: "Cadastrado por", visible: true},
    responsible: {label: "Realizado por", visible: true}
};

const SosHistory = () => {
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();

    const {
        sosHistoryActions, sosHistoryProtectionPrograms,
        sosHistoryCitizens, sosHistoryCreatedBy, sosHistoryResponsible
    } = useReportFiltersFetch({
        fetchSosHistoryActions: true, fetchSosHistoryProtectionPrograms: true,
        fetchSosHistoryCitizens: true, fetchSosHistoryCreatedBy: true, fetchSosHistoryResponsible: true
    });

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);
    const [tableColumns, setTableColumns] = useState(() => Object.fromEntries(
        Object.entries(initialTableColumns).map(([id, column]) => [id, {...column}])
    ));

    const [title] = useState("Histórico operacional do SOS");
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        const encodedFilters = searchParams.get("d");
        let urlFilters = {};

        try {
            if (encodedFilters) urlFilters = JSON.parse(decodeURIComponent(atob(encodedFilters)));
        } catch {
            urlFilters = {};
        }

        const citizenId = urlFilters.citizenId ?? searchParams.get("id");
        const hasActions = Array.isArray(urlFilters.actions);
        const hasCreatedBy = Array.isArray(urlFilters.createdByIds);

        if (!encodedFilters && !citizenId) return;
        if (
            (hasActions && urlFilters.actions.length && !sosHistoryActions.length)
            || (hasCreatedBy && urlFilters.createdByIds.length && !sosHistoryCreatedBy.length)
            || (citizenId && !sosHistoryCitizens.length)
        ) return;

        try {
            const filtersFromUrl = {
                ...initialFilters(),
                dtStart: urlFilters.dtStart ? new Date(urlFilters.dtStart) : filters.dtStart,
                dtEnd: urlFilters.dtEnd ? new Date(urlFilters.dtEnd) : filters.dtEnd,
                actions: hasActions ? sosHistoryActions.filter(option => urlFilters.actions.map(String).includes(String(option.value))) : [],
                createdByIds: hasCreatedBy ? sosHistoryCreatedBy.filter(option => urlFilters.createdByIds.map(String).includes(String(option.value))) : [],
                citizenId: citizenId ? sosHistoryCitizens.find(option => String(option.value) === String(citizenId)) ?? null : null
            };

            setFilters(filtersFromUrl);
            apply(filtersFromUrl, Boolean(encodedFilters));
        } catch (error) {
            console.error(error);
        }

    }, [searchParams, sosHistoryActions, sosHistoryCreatedBy, sosHistoryCitizens]);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async (filtersToApply = filters, ignorePeriodLimit = false) => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.validateOnlyDates(
                filtersToApply.dtStart,
                filtersToApply.dtEnd,
                ignorePeriodLimit || filtersToApply.citizenId ? null : 1,
                'year'
            );

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filtersToApply.dtStart ? ReportUtils.formatDateTime(filtersToApply.dtStart) : null,
                dtEnd: filtersToApply.dtEnd ? ReportUtils.formatDateTime(filtersToApply.dtEnd) : null,
                actions: filtersToApply.actions?.map(i => i.value) ?? [],
                programs: filtersToApply.programs?.map(i => i.value) ?? [],
                citizenId: filtersToApply.citizenId?.value ?? null,
                createdByIds: filtersToApply.createdByIds?.map(i => i.value) ?? [],
                responsibleIds: filtersToApply.responsibleIds?.map(i => i.value) ?? []
            };

            const auditFilters = ReportUtils.filters({
                ...filtersToApply,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/sos_history/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useGlobalKey("Enter", () => apply());

    const clearSearch = () => {
        setFilters(initialFilters);
        ReportUtils.clearUrlFilters();
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <div className={"flex"}>

            <Template isLoading={loading}>
                <Template.Header>
                    <div className="flex justify-between w-full">
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            visibilityColumns={true}
                            columns={tableColumns}
                            setColumns={setTableColumns}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={reportData?.show_report === true && 1}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </div>
                </Template.Header>
                <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                    <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                          collapsed={collapsed} apply={() => apply()} clearSearch={clearSearch}>

                        <Filters setFilters={setFilters} filters={filters} sosHistoryActions={sosHistoryActions}
                            sosHistoryProtectionPrograms={sosHistoryProtectionPrograms} sosHistoryCitizens={sosHistoryCitizens}
                            sosHistoryCreatedBy={sosHistoryCreatedBy} sosHistoryResponsible={sosHistoryResponsible}/>

                        <AutoDatetime setFilters={setFilters}/>

                    </Menu>
                </Template.Body>
            </Template>

            <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.show_report === false ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.show_report === false ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                <Card.Body>
                    <div className={`flex-1 ${!reportData || reportData?.show_report === false ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                        <NotAppliedContent reportData={reportData}/>
                        <NoDataContent reportData={reportData} data={reportData?.show_report === false && 0}/>
                        <Content reportData={reportData} data={reportData?.show_report === true} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} tableColumns={tableColumns}/>
                    </div>
                </Card.Body>
            </Card>

        </div>
    );
};

export default SosHistory;